import { Grid, makeStyles, Typography, CircularProgress, Backdrop } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { downloadCustomUploadedFile } from "../../../../../../containers/actions/common/fileActions";
import { fetchDSBatchDetails } from "../../../../../../containers/actions/common/saslAction";
import { useDispatch } from "react-redux";
import ShowFiles from "../../../../models/files/ShowFiles";
import GetAppIcon from "@material-ui/icons/GetApp";
import ViewStructuredInformationBox from "./ViewStructuredInformationBox";
import { ReactComponent as StructureIcon } from "./../../../../../layout/icons/smc_fill.svg";
import ViewConsumption from "./ViewConsumption";
import UpdateBMRData from "../BMRConsumption/UpdateBMRData";

// Import all digitized components
import DigitizedCommercialInvoice from "../../../../Digitisation/PCIDublin/DigitizedCommercialInvoice";
import PatheonDigitizedCommercialInvoice from "../../../../Digitisation/DigitizedCommercialInvoice";
import DigitizedDeliveryNote from "../../../../Digitisation/PCIDublin/DigitizedDeliveryNote";
import DigitizedPackingList from "../../../../Digitisation/PCIDublin/DigitizedPackingList";
import FinishedProductCheckList from "../../../../Digitisation/PCIDublin/FinishedProductCheckList";
import PartialManufacturingMedicinalProduct from "../../../../Digitisation/PCIDublin/PartialManufacturingMedicinalProduct";

// Import new document components
import CertificateOfAnalysis from "../../../../Digitisation/CertificateOfAnalysis";
import ManufactureCertificate from "../../../../Digitisation/ManufactureCertificate";
import BillOfLading from "../../../../Digitisation/BillOfLading";
import DigitizedPackingSlip from "../../../../Digitisation/DigitizedPackingSlip";
import DigitizedConsumption from "../../../../Digitisation/DigitizedConsumption";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Hidden,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  link: {
    color: "blue",
    cursor: "pointer",
    textDecoration: "underline",
    padding: "5px 0px",
  },
  titleGrid: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    backgroundColor: "#f5f9ff",
    width: "100%",
  },
  closeText: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: 500,
    "&:hover": {
      borderBottom: "2px solid red",
    },
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    cursor: "pointer",
    padding: "0.3rem",
  },
  title: {
    fontWeight: 500,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  main: {
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const StructuredInformationFiles = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    structuredFiles,
    downloadFiles,
    batchDetails,
    schrockenDeliveryScheduleID,
    consumption,
    targetQuantity, 
    updatedFileName, 
    updatedDigitizedName, 
    documentCategory,
    digitizedData,
    isDuplicateDigitized,
    poNumber,
    batchNumber
  } = props;

  const [viewStructuredData, setViewStructuredData] = useState(false);
  const [viewDigitizedDocument, setViewDigitizedDocument] = useState(false);
  const [parsedKvpData, setParsedKvpData] = useState(null);
  const [kvpParseError, setKvpParseError] = useState(null);

  const [viewStructuredConsumption, setViewStructuredConsumption] =
    useState(false);

  const [openConsumption, setOpenConsumption] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleCloseSA = () => {
    setOpenConsumption(false);
  };

  // Parse kvpJson when digitizedData changes
  useEffect(() => {
    if (digitizedData && digitizedData.kvpJson) {
      try {
        setKvpParseError(null);
        const parsed = JSON.parse(digitizedData.kvpJson);
        setParsedKvpData(parsed);
      } catch (error) {
        console.error("Error parsing kvpJson:", error);
        setKvpParseError(error.message);
        setParsedKvpData(null);
        
        // Try to extract data from original structure if available
        if (digitizedData.originalDigitizedData) {
          // Use original digitized data as fallback
        }
      }
    } else {
      setParsedKvpData(null);
      setKvpParseError(null);
    }
  }, [digitizedData]);

  const handleDownloadFile = (schrockenCustomFileID, type) => {
    const data = {
      schrockenCustomFileID,
    };
    dispatch(downloadCustomUploadedFile(data, type));
  };

  const handleOpenCloseViewStructuredData = () => {
    setViewStructuredData(!viewStructuredData);
  };

  const handleOpenCloseViewDigitizedDocument = () => {
    setViewDigitizedDocument(!viewDigitizedDocument);
  };

  const handleOpenCloseViewStructuredConsumption = () => {
    setViewStructuredConsumption(!viewStructuredConsumption);
  };

  // Refresh callback to fetch latest data after successful save
  const handleDataRefresh = () => {
    return new Promise((resolve) => {
      if (batchDetails?.schrockenDeliveryScheduleBatchID) {
        setIsRefreshing(true);
        try {
          // Dispatch the fetch action with proper payload object
          const refreshData = {
            schrockenDeliveryScheduleBatchID: batchDetails.schrockenDeliveryScheduleBatchID,
          };
          dispatch(fetchDSBatchDetails(refreshData));
          
          // Give time for the action to complete and data to update
          setTimeout(() => {
            setIsRefreshing(false);
            resolve();
          }, 1000);
        } catch (error) {
          console.error('Error refreshing data:', error);
          setIsRefreshing(false);
          resolve();
        }
      } else {
        resolve();
      }
    });
  };


  const renderDigitizedComponent = () => {

    if (kvpParseError) {
      return (
        <Box p={3}>
          <Typography variant="h6" color="error">Error Processing Document</Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
            Failed to parse document data: {kvpParseError}
          </Typography>
          {digitizedData?.originalDigitizedData && (
            <>
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Raw document data:
              </Typography>
              <pre style={{ marginTop: 8, padding: 16, backgroundColor: "#f5f5f5", borderRadius: 4, fontSize: "12px" }}>
                {JSON.stringify(digitizedData.originalDigitizedData, null, 2)}
              </pre>
            </>
          )}
        </Box>
      );
    }

    // Special handling for PackingSlip - data comes from batchDetails.digitizedExtractedData
    if (digitizedData?.documentType === "PackiingSlip") {
      if (!batchDetails?.digitizedExtractedData) {
        return (
          <Box p={3}>
            <Typography variant="h6">Loading Document...</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Processing digitized document data...
            </Typography>
          </Box>
        );
      }
      
      // Find packing slip data in digitizedExtractedData
      const packingSlipDoc = batchDetails.digitizedExtractedData.find(doc => doc.documentType === "PackingSlip");
      if (!packingSlipDoc?.packingSlipJSONOriginal) {
      
        return (
          <Box p={3}>
            <Typography variant="h6">No Packing Slip Data</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Packing slip data not found in the batch details.
            </Typography>
          </Box>
        );
      }
    } else {
      // For other document types, use the original logic
      if (!parsedKvpData || !digitizedData) {
        return (
          <Box p={3}>
            <Typography variant="h6">Loading Document...</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Processing digitized document data...
            </Typography>
          </Box>
        );
      }
    }

    // // Special handling for PackingSlip data source
    let initialData = parsedKvpData;
    // if (digitizedData?.documentType === "PackingSlip") {
    //   const packingSlipDoc = batchDetails.digitizedExtractedData.find(doc => doc.documentType === "PackingSlip");
    //   initialData = packingSlipDoc?.packingSlipJSONOriginal || {};
    // }

    const commonProps = {
      initialData: initialData,
      onClose: handleOpenCloseViewDigitizedDocument,
      schrockenCustomFileID: digitizedData.schrockenCustomFileID,
      digitizedData: digitizedData,
      batchDetails: batchDetails,
      poNumber: poNumber,
      batchNumber: batchNumber,
      onDataRefresh: handleDataRefresh
    };

    switch (digitizedData.documentType) {
      // New document types
      case "CertificateOfAnalysis":
        return <CertificateOfAnalysis {...commonProps} />;
      case "CertificateOfManufacture":
        return <ManufactureCertificate {...commonProps} />;
      case "CommercialInvoice":
        // Check if processedFileName contains "Patheon" to determine which component to use
        if (digitizedData.processedFileName && digitizedData.processedFileName.includes("Patheon")) {
          return <PatheonDigitizedCommercialInvoice {...commonProps} />;
        } else {
          return <DigitizedCommercialInvoice {...commonProps} />;
        }
      case "BillOfLading":
        return <BillOfLading {...commonProps} />;
      case "PackingSlip":
        return <DigitizedPackingSlip {...commonProps} />;
        
      // Existing document types (preserved for backward compatibility)
      case "DeliveryNote":
        return <DigitizedDeliveryNote {...commonProps} />;
      case "PackingList":
        return <DigitizedPackingList {...commonProps} />;
      case "ConfirmationOfPartialManufacturingOfAnMedicinalProduct":
        return <PartialManufacturingMedicinalProduct {...commonProps} />;
      case "FinishedProductDispatchCheckList":
        return <FinishedProductCheckList {...commonProps} />;
        
      // ConsumptionDetails might need a custom component later
      case "ConsumptionDetails":
        return <DigitizedConsumption {...commonProps} />;
        
      default:
        return (
          <Box p={3}>
            <Typography variant="h6">Unknown Document Type</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Document type "{digitizedData.documentType}" is not supported. Raw data:
            </Typography>
            <pre style={{ marginTop: 16, padding: 16, backgroundColor: "#f5f5f5", borderRadius: 4, fontSize: "12px" }}>
              {JSON.stringify(parsedKvpData, null, 2)}
            </pre>
          </Box>
        );
    }
  };

  return (
    <Grid container spacing={3} justifyContent="space-between">
      <Grid item xs={12} sm={6} md={6}>
        <Typography
          variant="body2"
          style={{ fontWeight: 500, paddingTop: "1rem" }}
        >
          Download document
        </Typography>
        {downloadFiles.length > 0 ? (
          downloadFiles.map((file, index) => (
            <Grid
              item
              xs={12}
              key={index}
              style={{ display: "flex", alignItems: "center" }}
            >
              <GetAppIcon fontSize="small" style={{ color: "blue" }} />
              <Typography
                variant={"body2"}
                className={classes.link}
                style={{
                  textDecoration: "underline",
                }}
                onClick={() => handleDownloadFile(file.schrockenCustomFileID)}
              >
                {updatedFileName}
              </Typography>
            </Grid>
          ))
        ) : (
          <Typography variant="body2">-No Documents Uploaded-</Typography>
        )}
      </Grid>

      {/* Existing structured data section (preserve original logic) */}
      {!digitizedData && structuredFiles && !consumption &&
        (batchDetails?.coaJSONEdited || batchDetails?.coaJSONOriginal) ? (
        <Grid item xs={12} sm={6} md={6}>
          <Typography
            variant="body2"
            style={{ fontWeight: 500, paddingTop: "1rem" }}
          >
            View digitized document
          </Typography>
          <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
            <GetAppIcon fontSize="small" style={{ color: "blue" }} />
            <Typography
              variant={"body2"}
              className={classes.link}
              style={{
                textDecoration: "underline",
              }}
              onClick={() => handleOpenCloseViewStructuredData()}
            >
              {updatedDigitizedName}
            </Typography>
            <StructureIcon
              style={{ cursor: "pointer" }}
              onClick={() => handleOpenCloseViewStructuredData()}
            />
          </Grid>
          
          {/* Display review fields information for old structured data */}
          {(batchDetails?.coaJSONEdited || batchDetails?.coaJSONOriginal) && (
            <>
              <Typography variant="body2" style={{ color: "#666", marginTop: "0.5rem" }}>
                No. of fields that require review - {
                  (batchDetails?.coaJSONEdited?.noOfFieldsToBeReviewed) || 
                  (batchDetails?.coaJSONOriginal?.noOfFieldsToBeReviewed) || 
                  0
                }
              </Typography>
              <Typography variant="body2" style={{ color: "#666" }}>
                No. of fields reviewed - {
                  (batchDetails?.coaJSONEdited?.noOfFieldsReviewed) || 
                  (batchDetails?.coaJSONOriginal?.noOfFieldsReviewed) || 
                  0
                }
              </Typography>
            </>
          )}
        </Grid>
      ) :
        // Show "Processing data" only when there's no digitized data AND no old structured data
        !digitizedData && structuredFiles && !consumption &&
          downloadFiles.length > 0 &&
          !batchDetails?.coaJSONEdited &&
          !batchDetails?.coaJSONOriginal ? (
          <>
            <Grid item xs={12} sm={6} md={6}>
              <Typography
                variant="body2"
                style={{ fontWeight: 500, paddingTop: "1rem" }}
              >
                View digitized document
              </Typography>
              <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant={"body2"}
                  className={classes.link}
                  style={{
                    textDecoration: "underline",
                  }}
                >
                  ...Processing data
                </Typography>
              </Grid>
            </Grid>
          </>
        ) : null}

      {/* New section for digitized documents */}
      {digitizedData && (
        <>
          <Grid item xs={12} sm={6} md={6}>
            <Typography
              variant="body2"
              style={{ fontWeight: 500, paddingTop: "1rem" }}
            >
              View digitized document
            </Typography>
            <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
              <GetAppIcon fontSize="small" style={{ color: "blue" }} />
              <Typography
                variant={"body2"}
                className={classes.link}
                style={{
                  textDecoration: "underline",
                  color: kvpParseError ? "red" : "blue"
                }}
                onClick={() => handleOpenCloseViewDigitizedDocument()}
              >
                {kvpParseError ? `${updatedDigitizedName} (Error)` : updatedDigitizedName}
              </Typography>
              <StructureIcon
                style={{ cursor: "pointer" }}
                onClick={() => handleOpenCloseViewDigitizedDocument()}
              />
            </Grid>
            
            {/* Display review fields information */}
            {(() => {
              // Function to get review data based on document type
              const getReviewData = () => {
                const docType = digitizedData?.documentType;
                
                // First, check if there's edited JSON for this document type
                if (docType === "CertificateOfManufacture" && batchDetails?.comJSONEdited) {
                  return {
                    noOfFieldsToBeReviewed: batchDetails.comJSONEdited.noOfFieldsToBeReviewed || 0,
                    noOfFieldsReviewed: batchDetails.comJSONEdited.noOfFieldsReviewed || 0
                  };
                }
                
                if (docType === "CommercialInvoice" && batchDetails?.commercialInvoiceJSONEdited) {
                  return {
                    noOfFieldsToBeReviewed: batchDetails.commercialInvoiceJSONEdited.noOfFieldsToBeReviewed || 0,
                    noOfFieldsReviewed: batchDetails.commercialInvoiceJSONEdited.noOfFieldsReviewed || 0
                  };
                }
                
                if (docType === "BillOfLading" && batchDetails?.billOfLadingJSONEdited) {
                  return {
                    noOfFieldsToBeReviewed: batchDetails.billOfLadingJSONEdited.noOfFieldsToBeReviewed || 0,
                    noOfFieldsReviewed: batchDetails.billOfLadingJSONEdited.noOfFieldsReviewed || 0
                  };
                }
                
                if (docType === "PackingSlip" && batchDetails?.packingSlipJSONEdited) {
                  return {
                    noOfFieldsToBeReviewed: batchDetails.packingSlipJSONEdited.noOfFieldsToBeReviewed || 0,
                    noOfFieldsReviewed: batchDetails.packingSlipJSONEdited.noOfFieldsReviewed || 0
                  };
                }
                
                if (docType === "ConsumptionDetails" && batchDetails?.consumptionJSONEdited) {
                  return {
                    noOfFieldsToBeReviewed: batchDetails.consumptionJSONEdited.noOfFieldsToBeReviewed || 0,
                    noOfFieldsReviewed: batchDetails.consumptionJSONEdited.noOfFieldsReviewed || 0
                  };
                }
                
                // If no edited JSON exists, fall back to original data from digitizedExtractedData
                if (docType === "PackingSlip") {
                  const packingSlipDoc = batchDetails?.digitizedExtractedData?.find(doc => doc.documentType === "PackingSlip");
                  const packingSlipData = packingSlipDoc?.packingSlipJSONOriginal;
                  
                  if (packingSlipData) {
                    return {
                      noOfFieldsToBeReviewed: packingSlipData.noOfFieldsToBeReviewed || 0,
                      noOfFieldsReviewed: packingSlipData.noOfFieldsReviewed || 0
                    };
                  }
                }
                
                if (docType === "BillOfLading") {
                  const billOfLadingDoc = batchDetails?.digitizedExtractedData?.find(doc => doc.documentType === "BillOfLading");
                  const billOfLadingData = billOfLadingDoc?.billOfLadingJSONOriginal;
                  
                  if (billOfLadingData) {
                    return {
                      noOfFieldsToBeReviewed: billOfLadingData.noOfFieldsToBeReviewed || 0,
                      noOfFieldsReviewed: billOfLadingData.noOfFieldsReviewed || 0
                    };
                  }
                }
                
                if (docType === "CommercialInvoice") {
                  const commercialInvoiceDoc = batchDetails?.digitizedExtractedData?.find(doc => doc.documentType === "CommercialInvoice");
                  const commercialInvoiceData = commercialInvoiceDoc?.CommericalInvoiceJSONOriginal;
                  
                  if (commercialInvoiceData) {
                    return {
                      noOfFieldsToBeReviewed: commercialInvoiceData.noOfFieldsToBeReviewed || 0,
                      noOfFieldsReviewed: commercialInvoiceData.noOfFieldsReviewed || 0
                    };
                  }
                }
                
                if (docType === "CertificateOfManufacture") {
                  const certificateDoc = batchDetails?.digitizedExtractedData?.find(doc => doc.documentType === "CertificateOfManufacture");
                  const certificateData = certificateDoc?.CoMJSONOriginal;
                  
                  if (certificateData) {
                    return {
                      noOfFieldsToBeReviewed: certificateData.noOfFieldsToBeReviewed || 0,
                      noOfFieldsReviewed: certificateData.noOfFieldsReviewed || 0
                    };
                  }
                }
                
                if (docType === "ConsumptionDetails") {
                  const consumptionDoc = batchDetails?.digitizedExtractedData?.find(doc => doc.documentType === "ConsumptionDetails");
                  const consumptionData = consumptionDoc?.ConsumptionDetailsJSONOriginal;
                  
                  if (consumptionData) {
                    return {
                      noOfFieldsToBeReviewed: consumptionData.noOfFieldsToBeReviewed || 0,
                      noOfFieldsReviewed: consumptionData.noOfFieldsReviewed || 0
                    };
                  }
                }
                
                // Final fallback to parsedKvpData
                if (parsedKvpData) {
                  return {
                    noOfFieldsToBeReviewed: parsedKvpData.noOfFieldsToBeReviewed || 0,
                    noOfFieldsReviewed: parsedKvpData.noOfFieldsReviewed || 0
                  };
                }
                
                return null;
              };
              
              const reviewData = getReviewData();
              
              if (reviewData) {
                return (
                  <>
                    <Typography variant="body2" style={{ color: "#666", marginTop: "0.5rem" }}>
                      No. of fields that require review - {reviewData.noOfFieldsToBeReviewed}
                    </Typography>
                    <Typography variant="body2" style={{ color: "#666" }}>
                      No. of fields reviewed - {reviewData.noOfFieldsReviewed}
                    </Typography>
                  </>
                );
              }
              
              return null;
            })()}
          </Grid>
        </>
      )}

      {consumption && downloadFiles.length > 0 && !digitizedData ? (
        <Grid item xs={12} sm={6} md={6}>
          <Typography
            variant="body2"
            style={{ fontWeight: 500, paddingTop: "1rem" }}
          >
            View digitized document
          </Typography>
          {batchDetails.bomConsumptionAddedIndicator === true &&
            downloadFiles.length > 0 ? (
            <Grid
              item
              xs={12}
              style={{ display: "flex", alignItems: "center" }}
            >
              <GetAppIcon fontSize="small" style={{ color: "blue" }} />
              <Typography
                variant={"body2"}
                className={classes.link}
                style={{
                  textDecoration: "underline",
                }}
                onClick={() => setOpenConsumption(true)}
              >
                Consumption_pages
              </Typography>
              <StructureIcon
                style={{ cursor: "pointer" }}
                onClick={() => setOpenConsumption(true)}
              />
            </Grid>
          ) : (
            <Typography variant="body2">NA</Typography>
          )}
        </Grid>
      ) : null}

      {viewStructuredData && (
        <ViewStructuredInformationBox
          coaJSONEdited={batchDetails?.coaJSONEdited}
          batchDetails={batchDetails}
          coaJSONOriginal={batchDetails?.coaJSONOriginal}
          schrockenCustomFileID={
            structuredFiles?.schrockenCustomFileID
              ? structuredFiles?.schrockenCustomFileID
              : null
          }
          open={viewStructuredData}
          onClose={handleOpenCloseViewStructuredData}
          structuredFiles={structuredFiles}
          documentCategory={documentCategory} />
      )}

      {/* Modal for digitized documents */}
      {viewDigitizedDocument && (
        <Dialog fullScreen open={viewDigitizedDocument} onClose={handleOpenCloseViewDigitizedDocument}>
          <DialogTitle disableTypography style={{ backgroundColor: "#f5f9ff" }}>
            <Grid container justifyContent="center" className={classes.titleGrid}>
              <Grid item xs={11} sm={11} md={11} className={classes.main}>
                <Typography variant="h4" className={classes.title}>
                  {documentCategory}
                </Typography>
              </Grid>
              <Grid item xs={1} sm={1} md={1}>
                <Hidden xsDown implementation="css">
                  <Typography
                    variant="body2"
                    onClick={handleOpenCloseViewDigitizedDocument}
                    className={classes.closeText}
                  >
                    Cancel{" "}
                    <CloseIcon
                      fontSize="large"
                      color="error"
                      className={classes.icon}
                    />
                  </Typography>
                </Hidden>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Box>
              {renderDigitizedComponent()}
            </Box>
          </DialogContent>
        </Dialog>
      )}
      
      {viewStructuredConsumption && (
        <ViewConsumption
          open={viewStructuredConsumption}
          batchDetails={batchDetails}
          onClose={handleOpenCloseViewStructuredConsumption}
          schrockenDeliveryScheduleID={schrockenDeliveryScheduleID}
        />
      )}

      {openConsumption && (
        <UpdateBMRData
          {...props}
          schrockenDeliveryScheduleBatchID={
            batchDetails?.schrockenDeliveryScheduleBatchID
          }
          schrockenDeliveryScheduleID={
            batchDetails?.schrockenDeliveryScheduleID
          }
          bomConsumptionAddedIndicator={true}
          open={openConsumption}
          onClose={handleCloseSA}
          handleClose={handleCloseSA}
          schrockenCustomFileID={
            downloadFiles.length > 0
              ? downloadFiles[0].schrockenCustomFileID
              : ""
          }
          batchDetails={batchDetails}
          targetQuantity={targetQuantity}
          viewFlag={true}
        />
      )}

      {/* Loading backdrop when refreshing data */}
      <Backdrop
        open={isRefreshing}
        style={{ 
          zIndex: 9999, 
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography 
            variant="h6" 
            style={{ 
              marginTop: 16, 
              fontWeight: 500 
            }}
          >
            Refreshing data...
          </Typography>
        </div>
      </Backdrop>
    </Grid>
  );
};

export default StructuredInformationFiles;
